import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Input, Modal, Popover, List, Tag, Spin, Image, message } from 'antd';
import { AimOutlined, CloseOutlined, CheckOutlined, DeleteOutlined, PlusCircleOutlined, UploadOutlined } from '@ant-design/icons';
import type { Annotation } from './types';
import { bugReportApi } from '../../services/bugReportApi';
import './BugReportOverlay.css';

const { TextArea } = Input;

interface BugReportOverlayProps {
  onSubmit: (annotations: Annotation[], screenshots: string[]) => void;
  onCancel: () => void;
}

interface PendingAnnotation {
  info: Omit<Annotation, 'description'>;
  element: HTMLElement | null;
  isPositional: boolean; // true = right-click position mark
  clickX: number;
  clickY: number;
}

function generateSelector(el: HTMLElement): string {
  const parts: string[] = [];
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector = `#${current.id}`;
      parts.unshift(selector);
      break;
    }
    const classes = Array.from(current.classList)
      .filter(c => !c.startsWith('bug-') && c !== 'ant-popover-open')
      .slice(0, 3);
    if (classes.length > 0) {
      selector += '.' + classes.join('.');
    } else if (current.parentElement) {
      const siblings = Array.from(current.parentElement.children);
      const index = siblings.indexOf(current) + 1;
      selector += `:nth-child(${index})`;
    }
    parts.unshift(selector);
    current = current.parentElement;
    if (parts.length >= 4) break;
  }
  return parts.join(' > ');
}

function captureElementInfo(el: HTMLElement): Omit<Annotation, 'description'> {
  const rect = el.getBoundingClientRect();
  const formItem = el.closest('.ant-form-item');
  const label = formItem?.querySelector('label')?.textContent || '';

  return {
    selector: generateSelector(el),
    elementTag: el.tagName.toLowerCase(),
    elementText: (el.textContent || '').slice(0, 50).trim(),
    label,
    rect: {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
  };
}

const BugReportOverlay: React.FC<BugReportOverlayProps> = ({ onSubmit, onCancel }) => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [pending, setPending] = useState<PendingAnnotation | null>(null);
  const [description, setDescription] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const badgeRefs = useRef<HTMLDivElement[]>([]);
  const [screenshotUrls, setScreenshotUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleScreenshotUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.warning('Only image files are supported');
      return;
    }
    setUploading(true);
    try {
      const result = await bugReportApi.uploadScreenshot(file);
      setScreenshotUrls(prev => [...prev, result.url]);
      message.success(`Screenshot uploaded: ${result.fileName}`);
    } catch (err) {
      console.error('Screenshot upload error:', err);
      message.error('Screenshot upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleRemoveScreenshot = useCallback((index: number) => {
    setScreenshotUrls(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Paste handler for clipboard screenshots (always active in annotation mode)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            handleScreenshotUpload(file);
          }
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleScreenshotUpload]);

  // Add/remove body class for hover styles
  useEffect(() => {
    document.body.classList.add('bug-report-mode');
    return () => {
      document.body.classList.remove('bug-report-mode');
      document.querySelectorAll('.bug-annotated').forEach(el => {
        el.classList.remove('bug-annotated');
      });
      badgeRefs.current.forEach(badge => badge.remove());
      badgeRefs.current = [];
    };
  }, []);

  const isUIElement = (target: HTMLElement) =>
    target.closest('.bug-report-toolbar') ||
    target.closest('.ant-popover') ||
    target.closest('.ant-modal-root') ||
    target.closest('.ant-modal-wrap') ||
    target.closest('.ant-modal') ||
    target.closest('.ant-modal-mask') ||
    target.closest('.bug-annotation-badge');

  // Left-click: select element
  const handleDocumentClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (isUIElement(target)) return;

    e.preventDefault();
    e.stopPropagation();

    const info = captureElementInfo(target);
    setPending({
      info,
      element: target,
      isPositional: false,
      clickX: e.clientX,
      clickY: e.clientY,
    });
    setDescription('');
  }, []);

  // Right-click: position mark (for "add something here")
  const handleContextMenu = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (isUIElement(target)) return;

    e.preventDefault();
    e.stopPropagation();

    const nearestEl = target;
    const parentInfo = captureElementInfo(nearestEl);

    const info: Omit<Annotation, 'description'> = {
      selector: parentInfo.selector,
      elementTag: '(position)',
      elementText: '',
      label: '',
      rect: {
        x: Math.round(e.clientX),
        y: Math.round(e.clientY),
        width: 0,
        height: 0,
      },
    };

    setPending({
      info,
      element: null,
      isPositional: true,
      clickX: e.clientX,
      clickY: e.clientY,
    });
    setDescription('');
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick, true);
    document.addEventListener('contextmenu', handleContextMenu, true);
    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [handleDocumentClick, handleContextMenu]);

  const handleConfirmAnnotation = () => {
    if (!pending || !description.trim()) return;

    const annotation: Annotation = {
      ...pending.info,
      description: description.trim(),
    };

    const newAnnotations = [...annotations, annotation];
    setAnnotations(newAnnotations);

    // Visually mark
    const badge = document.createElement('div');
    badge.className = 'bug-annotation-badge';
    badge.textContent = String(newAnnotations.length);
    badge.style.position = 'fixed';
    badge.style.zIndex = '10000';

    if (pending.isPositional) {
      // Position marker: place badge at click position
      badge.style.top = `${pending.clickY - 10}px`;
      badge.style.left = `${pending.clickX - 10}px`;
    } else if (pending.element) {
      // Element marker: place badge at element corner
      pending.element.classList.add('bug-annotated');
      const rect = pending.element.getBoundingClientRect();
      badge.style.top = `${rect.top - 10}px`;
      badge.style.left = `${rect.right - 10}px`;
    }

    document.body.appendChild(badge);
    badgeRefs.current.push(badge);

    setPending(null);
    setDescription('');
  };

  const handleCancelAnnotation = () => {
    setPending(null);
    setDescription('');
  };

  const handleRemoveAnnotation = (index: number) => {
    setAnnotations(prev => prev.filter((_, i) => i !== index));
    if (badgeRefs.current[index]) {
      badgeRefs.current[index].remove();
      badgeRefs.current.splice(index, 1);
    }
    // Re-number remaining badges
    badgeRefs.current.forEach((badge, i) => {
      badge.textContent = String(i + 1);
    });
  };

  const handleFinish = () => {
    if (annotations.length === 0) return;
    setPending(null);
    setPreviewVisible(true);
  };

  const handleSubmit = () => {
    onSubmit(annotations, screenshotUrls);
    setPreviewVisible(false);
  };

  const popoverContent = pending ? (
    <div style={{ width: 340 }}>
      <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
        {pending.isPositional ? (
          <Tag color="purple"><PlusCircleOutlined /> 位置標註</Tag>
        ) : (
          <>
            {pending.info.label && <Tag color="blue">{pending.info.label}</Tag>}
            <code style={{ fontSize: 11 }}>{pending.info.elementTag}</code>
          </>
        )}
      </div>
      <TextArea
        autoFocus
        rows={3}
        placeholder={pending.isPositional ? '這裡需要新增什麼...' : '描述問題...'}
        value={description}
        onChange={e => setDescription(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleConfirmAnnotation();
          }
        }}
      />
      {/* Screenshot upload — inline with description */}
      <div style={{ marginTop: 8, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Button
            size="small"
            icon={<UploadOutlined />}
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}
          >
            上傳截圖
          </Button>
          <span style={{ fontSize: 11, color: '#8c8c8c' }}>或 Ctrl+V 貼上</span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleScreenshotUpload(file);
                e.target.value = '';
              }
            }}
          />
        </div>
        {uploading && (
          <div style={{ textAlign: 'center', padding: '4px 0' }}>
            <Spin size="small" /> <span style={{ marginLeft: 6, fontSize: 11 }}>上傳中...</span>
          </div>
        )}
        {screenshotUrls.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            <Image.PreviewGroup>
              {screenshotUrls.map((url, idx) => (
                <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
                  <Image
                    src={url}
                    width={60}
                    height={42}
                    style={{ objectFit: 'cover', borderRadius: 3, border: '1px solid #d9d9d9' }}
                  />
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => handleRemoveScreenshot(idx)}
                    style={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                      background: '#fff',
                      borderRadius: '50%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      width: 16,
                      height: 16,
                      padding: 0,
                      minWidth: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                    }}
                  />
                </div>
              ))}
            </Image.PreviewGroup>
          </div>
        )}
      </div>
      <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={handleCancelAnnotation}>
          取消
        </Button>
        <Button
          size="small"
          type="primary"
          disabled={!description.trim()}
          onClick={handleConfirmAnnotation}
        >
          確認
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Top toolbar */}
      <div className="bug-report-toolbar">
        <div className="toolbar-left">
          <AimOutlined />
          <span>標註模式 — 左鍵選元件標記問題，右鍵標記位置新增需求</span>
          {annotations.length > 0 && (
            <Tag color="red">{annotations.length} 個標註</Tag>
          )}
        </div>
        <div className="toolbar-right">
          <Button onClick={onCancel} icon={<CloseOutlined />}>
            取消
          </Button>
          <Button
            type="primary"
            danger
            onClick={handleFinish}
            disabled={annotations.length === 0}
            icon={<CheckOutlined />}
          >
            完成 ({annotations.length})
          </Button>
        </div>
      </div>

      {/* Popover for description input */}
      {pending && (
        <Popover
          open={!!pending}
          content={popoverContent}
          title={pending.isPositional ? '標記位置' : '標記問題'}
          trigger="click"
          placement="top"
          getPopupContainer={() => document.body}
        >
          <div
            style={{
              position: 'fixed',
              left: pending.isPositional
                ? pending.clickX
                : (pending.info.rect.x || 0),
              top: pending.isPositional
                ? pending.clickY + 4
                : (pending.info.rect.y || 0) + (pending.info.rect.height || 0) + 4,
              width: 1,
              height: 1,
              zIndex: 10002,
            }}
          />
        </Popover>
      )}

      {/* Preview Modal */}
      <Modal
        title="確認送出問題回報"
        open={previewVisible}
        onOk={handleSubmit}
        onCancel={() => setPreviewVisible(false)}
        okText="送出"
        cancelText="返回修改"
        width={600}
      >
        <List
          size="small"
          dataSource={annotations}
          renderItem={(item, index) => (
            <List.Item
              actions={[
                <Button
                  key="delete"
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveAnnotation(index)}
                />,
              ]}
            >
              <List.Item.Meta
                title={
                  <span>
                    <Tag color="red">#{index + 1}</Tag>
                    {item.elementTag === '(position)' ? (
                      <Tag color="purple"><PlusCircleOutlined /> 位置標註</Tag>
                    ) : (
                      <>
                        {item.label && <Tag color="blue">{item.label}</Tag>}
                        <code style={{ fontSize: 11, color: '#8c8c8c' }}>
                          {item.elementTag}
                        </code>
                      </>
                    )}
                  </span>
                }
                description={item.description}
              />
            </List.Item>
          )}
        />
        {screenshotUrls.length > 0 && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 6 }}>附帶截圖 ({screenshotUrls.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Image.PreviewGroup>
                {screenshotUrls.map((url, idx) => (
                  <Image
                    key={idx}
                    src={url}
                    width={100}
                    height={70}
                    style={{ objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  />
                ))}
              </Image.PreviewGroup>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default BugReportOverlay;
